import { inject, Injectable } from '@angular/core';

import { catchError, firstValueFrom, of } from 'rxjs';

import { CmsComponentNodeData, LinkObject } from '@models/interfaces';
import { DocumentReference } from '@interfaces/documents';
import { FileFile, FileFileData, FileService, MediaDocument, MediaDocumentData, MediaDocumentService } from '@generated/content';

import { BaseComponentResolver } from '@services/_base';
import { GatedFormTypesEnum } from '@models/enums';
import { HyphenatePipe } from '../../../pipes/hyphenate/hyphenate.pipe';
import { MimeTypeExtractor } from '@lib/utils/mime-type-extractor';
import { EnvironmentService } from '@services/environment/environment.service';
import { UrlFixedPipe } from '../../../pipes';

@Injectable()
export class DocumentResolver {
  private fileService = inject(FileService);
  private mediaDocumentService = inject(MediaDocumentService);

  public async getFileUrlForDocument(nodeUuid: string = null): Promise<string> {
    if (!nodeUuid) {
      return null;
    }

    const documentNode = await this.getDocumentNode(nodeUuid);
    const fileNode = await this.getFileNode(documentNode?.data?.relationships?.field_media_document?.data?.id);

    return fileNode ? fileNode?.data?.attributes?.uri?.url : null;
  }

  static buildDocumentTarget(documentId: string): LinkObject {
    const href = ['', 'site', 'files', 'pdfs', documentId].join('/');
    const target = EnvironmentService.isSafari ? '_self' : '_blank';

    return { href: UrlFixedPipe.transform(href), target };
  }

  public async getDocumentNode(nodeId: string = null): Promise<MediaDocument | null> {
    if (!nodeId) {
      return null;
    }

    return await firstValueFrom(this.mediaDocumentService.mediaDocumentEntityGet(nodeId).pipe(catchError(() => of(null))));
  }

  public async getFileNode(nodeId: string = null): Promise<FileFile | null> {
    if (!nodeId) {
      return null;
    }

    return await firstValueFrom(this.fileService.fileFileEntityGet(nodeId).pipe(catchError(() => of(null))));
  }

  static getDocumentReference(nodeId: string, nodePageData: CmsComponentNodeData[]): DocumentReference | null {
    const documentNode = this.getDocumentNode(nodeId, nodePageData);

    if (!documentNode) {
      return null;
    }

    const fileNode = this.getFileNode(documentNode?.relationships?.field_media_document?.data?.id, nodePageData);

    if (!fileNode) {
      return null;
    }

    const gated = (documentNode.attributes?.field_select_gated_form as GatedFormTypesEnum) ?? null;

    const metas = documentNode.attributes?.field_read_time;
    return {
      title: HyphenatePipe.transform(documentNode.attributes?.field_title),
      link: { ...this.buildDocumentTarget(documentNode.id), gated },
      metas: metas ? [metas] : null,
      mimeType: MimeTypeExtractor.getMimeType(fileNode.attributes?.filemime),
      documentType: documentNode.attributes?.field_document_type,
    };
  }

  static getFileNode(nodeId: string = null, nodePageData: CmsComponentNodeData[] = []): FileFileData | null {
    if (!nodeId || !nodePageData.length) {
      return null;
    }

    return BaseComponentResolver.getNode<FileFileData>(nodeId, FileFileData.TypeEnum.FileFile, nodePageData);
  }

  static getDocumentNode(nodeId: string = null, nodePageData: CmsComponentNodeData[] = []): MediaDocumentData | null {
    if (!nodeId || !nodePageData.length) {
      return null;
    }

    return BaseComponentResolver.getNode<MediaDocumentData>(nodeId, MediaDocumentData.TypeEnum.MediaDocument, nodePageData);
  }
}
